import { GoogleMap } from '@angular/google-maps';

interface ZoneCreateOptions {
    map: GoogleMap;
    onZoneComplete?: (path: any) => void;
}

export class ZoneCreate {
    private map: GoogleMap;
    private onZoneComplete: (path: any) => void;
    private points: { lat: number; lng: number }[] = [];
    private markers: google.maps.Marker[] = [];
    private polyline: google.maps.Polyline | null = null;
    private temporaryLine: google.maps.Polyline | null = null;
    private isDrawing: boolean = false;
    private mouseMoveListener: google.maps.MapsEventListener | null = null;
    private redoPoints: { lat: number; lng: number }[] = [];
    private svgIcon: any;
    private svgDuplicateIcon: any;

    constructor(options: ZoneCreateOptions) {
        this.map = options.map;
        this.onZoneComplete = options.onZoneComplete || (() => { });
        this.svgIcon = {
            path: 'M-15,0a15,15 0 1,0 30,0a15,15 0 1,0 -30,0', 
            fillColor: "white",
            fillOpacity: 1,
            strokeColor: "black",
            strokeWeight: 1,
            scale: 0.35, 
            anchor: new google.maps.Point(0, 0),
          };
        this.svgDuplicateIcon = { ...this.svgIcon, fillColor:"rgb(77, 76, 76)" };
    }

    enable() {
        if (this.isDrawing) return;

        this.isDrawing = true;

        this.polyline = new google.maps.Polyline({
            path: this.points,
            strokeColor: '#FF0000',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            map: this.map.googleMap,
        });

        this.temporaryLine = new google.maps.Polyline({
            strokeColor: '#FF0000',
            strokeOpacity: 0.5,
            strokeWeight: 2,
            map: this.map.googleMap,
        });

        // Track mouse movement for the dynamic last segment
        this.mouseMoveListener = google.maps.event.addListener(this.map.googleMap!, 'mousemove', (event: google.maps.MapMouseEvent) => {
            if (event.latLng && this.points.length > 0) {
                this.temporaryLine?.setMap(this.map.googleMap!);
                const dynamicPath = [...this.points, { lat: event.latLng.lat(), lng: event.latLng.lng() }];
                this.temporaryLine?.setPath(dynamicPath);
            } else if (this.points.length === 0) {
                this.temporaryLine?.setPath([]);
                this.temporaryLine?.setMap(null);
            }
        });
    }

    disable() {
        if (!this.isDrawing) return;

        this.isDrawing = false;
        this.points = [];
        this.markers.forEach((marker) => marker.setMap(null));
        this.markers = [];
        this.polyline?.setMap(null);
        this.polyline = null;
        this.temporaryLine?.setMap(null);
        this.temporaryLine = null;

        if (this.mouseMoveListener) {
            google.maps.event.removeListener(this.mouseMoveListener);
            this.mouseMoveListener = null;
        }
    }

    addMarker(point: { lat: number; lng: number }) {
        const marker = new google.maps.Marker({
            position: point,
            icon: this.svgIcon,
            map: this.map.googleMap,
        });
        
        marker.addListener('click', () => {
            if (this.points.length > 2 && this.isFirstPoint(point)) {
                this.points.pop();
                this.polyline?.setPath(this.points);
                this.completeZone();
            }
        });

        this.markers.push(marker);
    }

    round(num: number): number {
        return parseFloat(num.toFixed(7));
    }
    

    addPoint(lat: number, lng: number, close: boolean = false): void {
        const newPoint = { lat: this.round(lat), lng: this.round(lng)};
        if (this.points.length > 2 && this.isFirstPoint(newPoint) && close) {
            this.completeZone();
            return;
        }
    
        this.points.push(newPoint);
    
        this.addMarker(newPoint);
        this.markDuplicates();
    
        this.polyline?.setPath(this.points);
        this.redoPoints = [];
    }

    cancelAction(): void {
        if (this.points.length === 0) {
            return;
        }

        const removedPoint = this.points.pop();
        if (removedPoint) {
            this.redoPoints.unshift(removedPoint);
        }
        const removedMarker = this.markers.pop();
        if (removedMarker) {
            removedMarker.setMap(null);
        }
    
        this.markDuplicates();
        this.polyline?.setPath(this.points);
    }

    completeZone() {
        if (!this.isDrawing) return;

        this.onZoneComplete(this.polyline!.getPath());
        this.disable();
    }

    markDuplicates() {
        let dIndexes = this.getDuplicatePointIndexes();
        this.markers.forEach((marker, i) => {
            if (dIndexes.includes(i))
                marker.setIcon(this.svgDuplicateIcon);
            else
                marker.setIcon(this.svgIcon);
        })
    }

    redoAction() {
        if (this.redoPoints.length) {
            let point = this.redoPoints.shift();
            if (point) {
                this.points.push(point);
                this.addMarker(point);
                this.markDuplicates();
                this.polyline?.setPath(this.points);
            }
        }
    }

    getDuplicatePointIndexes(): number[] {
        let duplicates: number[] = [];
        const checkedPoints: Set<string> = new Set();
    
        for (let i = 0; i < this.points.length; i++) {
            const point = this.points[i];
            if (!point) continue;
            const key = `${point.lat}-${point.lng}`;
    
            if (!checkedPoints.has(key)) {
                const indexes = this.points
                    .map((p, index) => 
                        p && Math.abs(p.lat - point.lat) <= 1e-6 && 
                        Math.abs(p.lng - point.lng) <= 1e-6 ? index : -1
                    )
                    .filter(index => index !== -1);
    
                if (indexes.length > 1) {
                    duplicates = duplicates.concat(indexes);
                }
    
                checkedPoints.add(key);
            }
        }
    
        return duplicates;
    }

    isFirstPoint(point: { lat: number, lng: number }) {
        if (!this.points.length) return false;
        let tolerance = 1e-6;
        let firstPoint = this.points[0];
        if (!firstPoint) return false;
        return ( 
            Math.abs(firstPoint.lat - point.lat) <= tolerance && 
            Math.abs(firstPoint.lng - point.lng) <= tolerance
        );
    }
    
}
